/**
 * UnsubscribePage
 * =============================================================================
 * PUBLIC route at /unsubscribe/:token — renders a confirm screen.
 *
 * IMPORTANT: does NOT call the backend on mount. The Unsubscribe button must be
 * clicked explicitly before any backend call is made, so email-client link
 * prefetchers cannot auto-unsubscribe.
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authApi } from '../services/authApi';

type PageState = 'idle' | 'loading' | 'success' | 'error' | 'invalid';

const UnsubscribePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>(
    token && /^[a-f0-9]+$/i.test(token) ? 'idle' : 'invalid'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleUnsubscribe = useCallback(async () => {
    if (!token || state === 'loading') return;
    setState('loading');
    try {
      const result = await authApi.unsubscribe(token);
      if (result.unsubscribed) {
        setState('success');
      } else {
        // Token not found / already deactivated
        setErrorMessage('This unsubscribe link has already been used or is no longer valid.');
        setState('error');
      }
    } catch {
      setErrorMessage('Something went wrong. Please try again or contact support.');
      setState('error');
    }
  }, [token, state]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        {/* Logo */}
        <a href="/" className="inline-block mb-6 hover:opacity-80 transition-opacity">
          <img src="/logo.svg" alt="Precepi" className="h-6 mx-auto" />
        </a>

        {state === 'invalid' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Invalid link</h1>
            <p className="text-sm text-slate-500 mb-6">
              This unsubscribe link appears to be invalid or malformed. Please use the link from your email.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to Precepi
            </button>
          </>
        )}

        {state === 'idle' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
              <svg className="w-7 h-7 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Unsubscribe from notifications</h1>
            <p className="text-sm text-slate-500 mb-6">
              Click the button below to stop receiving label change notifications from Precepi. You can re-subscribe at any time from within the app.
            </p>
            <button
              onClick={handleUnsubscribe}
              className="w-full px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Unsubscribe
            </button>
            <button
              onClick={() => navigate('/')}
              className="mt-3 w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Cancel — keep my subscription
            </button>
          </>
        )}

        {state === 'loading' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
              <svg className="animate-spin w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Processing...</h1>
            <p className="text-sm text-slate-400">Please wait.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-50 rounded-full mb-4">
              <svg className="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Unsubscribed successfully</h1>
            <p className="text-sm text-slate-500 mb-6">
              You have been unsubscribed from Precepi notifications. You will no longer receive emails for this subscription.
              You can manage your subscriptions from within the app.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to Precepi
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Unable to unsubscribe</h1>
            <p className="text-sm text-slate-500 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to Precepi
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
